// Helpers to persist the user's location across an MSAL login round-trip.
// We use sessionStorage because:
//  - It is preserved across the full-page redirect to AAD and back (same origin/tab).
//  - It is not scoped by path like cookies written without an explicit `path: '/'`,
//    which previously caused the saved value to be invisible after AAD redirected
//    the user to a different path.

const URL_KEY = 'redirectUrl';
const HASH_KEY = 'redirectUrlHash';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

export function saveRedirectLocation(path?: string, hash?: string): void {
  if (!isBrowser()) return;
  try {
    const targetPath = path ?? window.location.pathname + window.location.search;
    const targetHash = hash ?? window.location.hash ?? '';
    window.sessionStorage.setItem(URL_KEY, targetPath || '/');
    window.sessionStorage.setItem(HASH_KEY, targetHash || '');
  } catch (_) {
    // sessionStorage may be unavailable (private mode); ignore
  }
}

export function consumeRedirectLocation(): { path: string | null; hash: string } {
  if (!isBrowser()) return { path: null, hash: '' };
  try {
    const path = window.sessionStorage.getItem(URL_KEY);
    const hash = window.sessionStorage.getItem(HASH_KEY) || '';
    window.sessionStorage.removeItem(URL_KEY);
    window.sessionStorage.removeItem(HASH_KEY);
    return { path, hash };
  } catch (_) {
    return { path: null, hash: '' };
  }
}

export function hasRedirectLocation(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.sessionStorage.getItem(URL_KEY) !== null;
  } catch (_) {
    return false;
  }
}

export function clearRedirectLocation(): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(URL_KEY);
    window.sessionStorage.removeItem(HASH_KEY);
  } catch (_) {}
}
